import styled, { css } from "styled-components";
import { BREAKPOINTS, desktop, mobile } from "../../variables/Breakpoints";
import { COLOURS, BORDERS } from "../../variables/Colors";
import { setFlex, setPosition } from "../../variables/Position";
import { noSelect } from "../../variables/Fonts";

const setPDPTileTheme = count => {
  switch (count) {
    case "0":
      return css`
        margin-bottom: 3rem;
        @media (max-width: ${BREAKPOINTS.sm}px) {
          margin-bottom: 0;
        }
        .roman-count {
          display: none;
        }
      `;
    case "1":
      return css`
        margin-bottom: 1rem;
        @media (max-width: ${BREAKPOINTS.sm}px) {
          ${props =>
            props.$background &&
            `background:             linear-gradient(to bottom, ${props.$background} 70%, white 30%)`};
          padding: 4rem 3rem 0 3rem;
          ${setFlex({ justify: "flex-start", align: "center" })};
          margin-bottom: 2.5rem;
        }
      `;
    default:
      return css`
        padding: 2rem;
        @media (max-width: ${BREAKPOINTS.sm}px) {
          padding: 0;
          margin-bottom: 2.5rem;
        }
      `;
  }
};

const setPDPContent = count => {
  switch (count) {
    case "1":
      return css`
        background-color: ${COLOURS.primary.white};
      `;
    default:
      return;
  }
};

const setPDPContentTheme = count => {
  switch (count) {
    case "0":
      return css`
        width: 60%;
        @media (max-width: ${BREAKPOINTS.sm}px) {
          width: 100%;
        }
      `;
    case "1":
      return css`
        margin: 0 3rem;
        @media (max-width: ${BREAKPOINTS.sm}px) {
          ${setPosition({ position: "absolute", bottom: 100, left: "0" })};
          margin: 1rem;
        }
      `;
    default:
      return css`
        @media (max-width: ${BREAKPOINTS.sm}px) {
          ${setPosition({ position: "relative", top: -30, zIndex: 2 })};
          margin: 0;
          width: 100%;
          height: 100%;
          background: inherit;
          overflow: hidden;
          &:before {
            ${setPosition({
              position: "absolute",
              top: "0",
              left: "0",
              right: "0",
              bottom: "0",
              zIndex: -1,
            })};
            content: "";
            will-change: transform;
            background: inherit;
            backdrop-filter: blur(10px);
            backface-visibility: hidden;
            transform: translate3d(0, 0, 0);
            -webkit-backface-visibility: hidden;
            -webkit-transform: translate3d(0, 0, 0);
            -webkit-backdrop-filter: blur(10px);
            background: linear-gradient(
              88deg,
              rgba(255, 255, 255, 0.1) 51.69%,
              rgba(255, 255, 255, 0) 150.79%
            );
          }
        }
      `;
  }
};

const setPDPTileImageTheme = count => {
  switch (count) {
    case "0":
      return css`
        width: 85%;
        @media (max-width: ${BREAKPOINTS.sm}px) {
          margin: 2rem 1rem 0 1rem;
          width: -webkit-fill-available;
          width: -moz-available;
        }
      `;
    case "1":
      return css`
        padding: 3rem 3rem 0 3rem;
        ${props =>
          props.$background && `background-color: ${props.$background}`};
        @media (max-width: ${BREAKPOINTS.sm}px) {
          padding: 0;
          margin-bottom: 2rem;
          width: 85vw;
          overflow: hidden;
          img {
            transform: scale(1.6);
            transform-origin: 50% 25%;
            min-height: 80vh;
            object-fit: cover;
          }
        }
      `;
    default:
      return css`
        @media (max-width: ${BREAKPOINTS.sm}px) {
          overflow: hidden;
          margin: 1rem;
          img {
            transform: scale(1.8);
            transform-origin: 50% 30%;
            min-height: 65vh;
            object-fit: cover;
          }
        } ;
      `;
  }
};

export const Headline = styled.h3`
  font-family: gantserif-medium-condensed;
  font-weight: 400;
  color: ${COLOURS.primary.black};
  margin: 0;
  text-align: left;
  font-size: 1.1rem;
  line-height: 1.2rem;
`;

export const PLPHeadline = styled(Headline)`
  @media (min-width: ${BREAKPOINTS.sm + 1}px) {
    font-size: calc((3vw / 100) * 50);
    line-height: calc((3vw / 100) * 50);
    &.expanded {
      font-size: calc((2.5vw / 100) * 70);
      line-height: calc((2.5vw / 100) * 70);
    }
    &.shrinked {
      font-size: calc((4vw / 100) * 30);
      line-height: calc((4vw / 100) * 30);
    }
  }
  @media (max-width: ${BREAKPOINTS.sm}px) {
    font-size: 1.1rem;
    line-height: 1.2rem;
  }
`;

export const Subheading = styled.h4`
  font-family: gantmodern-regular;
  font-weight: 400;
  color: ${COLOURS.primary.black};
  margin: 0;
  text-align: ${props => (props.$centerAlign ? "center" : "left")};
  font-size: 0.8rem;
  line-height: 0.8rem;
  ${props => props.$whiteWrap && "white-space: nowrap;"}
`;

export const Link = styled.a`
  font-family: gantmodern-regular;
  font-weight: 500;
  color: ${COLOURS.primary.black};
  text-decoration: underline;
`;

export const PDPSubheading = styled(Subheading)`
  @media (min-width: ${BREAKPOINTS.sm + 1}px) {
    font-size: calc((2vw / 100) * 50);
    line-height: calc((2vw / 100) * 50);
    &.expanded {
      font-size: calc((2vw / 100) * 70);
      line-height: calc((2vw / 100) * 70);
    }
    &.shrinked {
      font-size: calc((2vw / 100) * 30);
      line-height: calc((2vw / 100) * 30);
    }
  }
  @media (max-width: ${BREAKPOINTS.sm}px) {
    font-size: 0.8rem;
    line-height: 0.8rem;
  }
`;

export const Wrapper = styled.div`
  ${setPosition({ position: "relative" })};
  ${setFlex({ justify: "flex-start", align: "center", direction: "column" })};
  min-height: 100vh;
  height: 100%;
  width: 100%;
`;

export const Image = styled.img`
  ${props => props.$margin && "margin-bottom: 5rem;"};
  width: ${props => (props.$inheritWidth ? "inherit" : "100%")};
  height: 100%;
  max-width: unset;
  ${props =>
    props.$zIndexBoost && setPosition({ position: "relative", zIndex: 2 })};
  @media (max-width: ${BREAKPOINTS.sm}px) {
    width: 100%;
  }
  ${props => props.$desktop && desktop};
  ${props => props.$mobile && mobile};
`;

export const OverlayContainer = styled.div`
  ${setPosition({ position: "relative" })};
  ${props =>
    setFlex({
      justify: props.$justify,
      align: props.$align,
      direction: props.$direction,
    })};
  ${props =>
    props.$frost &&
    `
    background: linear-gradient(
        88deg,
        rgba(255, 255, 255, 0.1) 51.69%,
        rgba(255, 255, 255, 0) 150.79%
    );
    backdrop-filter: blur(10px);
    backface-visibility: hidden;
    transform:translate3d(0,0,0);
    -webkit-backface-visibility: hidden;
    -webkit-transform: translate3d(0,0,0);
    -webkit-backdrop-filter: blur(10px);
  `};
  ${props => props.$zIndexBoost && "z-index: 6;"};
  ${props => props.$border && `border: 1px solid ${BORDERS.secondary}`};
  min-height: 100vh;
  overflow: hidden;
  width: 100%;
  @media (min-width: ${BREAKPOINTS.sm + 1}px) {
    height: 100vh;
  }
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setFlex({ direction: "column" })};
    height: auto;
    overflow: visible;
  }
`;

export const Column = styled.div`
  ${setFlex({ direction: "column" })};
  ${props => props.$border && `border-right: 1px solid ${BORDERS.secondary}`};
  width: 50vw;
  height: 100vh;
  overflow-y: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  transform: translate3d(0, 0, 0);
  @media (max-width: ${BREAKPOINTS.sm}px) {
    width: 100vw;
    height: auto;
    overflow-y: visible;
  }
  ${props =>
    props.$positionColumn &&
    `
      @media (max-width: ${BREAKPOINTS.sm}px) {
        ${setPosition({ position: "relative", top: -100 })};
      }
  `};
  ${props => props.$desktop && desktop};
`;

export const ColumnContent = styled.div`
  height: auto;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  scrollbar-width: none;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    min-height: auto;
    overflow-x: visible;
  }
  ${props =>
    props.$flexGrow &&
    `
  ${setFlex({ direction: "column" })};
  `};
`;

export const Block = styled.div`
  ${setPosition({ position: "relative" })};
  ${props =>
    setFlex({
      justify: props.$justify || "center",
      align: props.$align || "center",
      direction: props.$direction,
    })};
  ${props =>
    props.$background &&
    `
      background-color: ${props.$background};
      @media (max-width: ${BREAKPOINTS.sm}px) {
        background: none;
      }
  `};
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setFlex({ justify: "center" })};
  }
  ${props =>
    props.$mobileMargin &&
    `
      @media (max-width: ${BREAKPOINTS.sm}px) {
        margin-top: 5rem;
      }
  `}
  ${props =>
    props.$flex &&
    `
  ${setFlex({})};
  flex: 1;
  `};
  ${props => props.$mobile && mobile};
`;

export const BlockContent = styled.div`
  ${setFlex({ justify: "center", direction: "column" })};
`;

export const ImageBlocks = styled.div`
  ${setFlex({ direction: "column" })};
  width: 100%;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    width: -webkit-fill-available;
    width: -moz-available;
  }
  ${props => props.$desktop && desktop};
`;

export const TertiaryImageBlock = styled(ImageBlocks)`
  ${setPosition({ position: "relative", top: -20 })};
  background-color: ${COLOURS.primary.white};
  width: 60%;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setPosition({ top: "0" })};
    margin: 5rem 0 0 0;
    width: 100%;
  }
`;

export const FrostBackdrop = styled.div`
  ${setPosition({ position: "absolute", right: "0", top: "0", zIndex: 3 })};
  ${props =>
    setFlex({
      justify: props.$justify || "center",
      align: props.$align || "center",
      direction: props.$direction,
    })};
  height: 100%;
  width: 40%;
  overflow: hidden;
  will-change: transform;
  background: inherit;
  &:before {
    ${setPosition({
      position: "absolute",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      zIndex: -1,
    })};
    content: "";
    background: inherit;
    backdrop-filter: blur(30px);
    backface-visibility: hidden;
    transform: translate3d(0, 0, 0);
    -webkit-backface-visibility: hidden;
    -webkit-transform: translate3d(0, 0, 0);
    -webkit-backdrop-filter: blur(30px);
    background: linear-gradient(
      88deg,
      rgba(255, 255, 255, 0.1) 51.69%,
      rgba(255, 255, 255, 0) 150.79%
    );
    border: 1px solid ${BORDERS.secondary};
    will-change: transform;
    @media (min-width: ${BREAKPOINTS.sm + 1}px) {
      border-right-width: 0;
    }
  }
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setFlex({ align: "center" })};
    height: 20vh;
    top: unset;
    bottom: 15vh;
    width: 50%;
  }
`;

export const SecondaryFrostBackdrop = styled(FrostBackdrop)`
  width: 60%;
  height: auto;
  overflow: visible;
  ${props =>
    props.$active &&
    setPosition({ position: "absolute", left: 20, bottom: "0", zIndex: 1 })};
  ${props =>
    !props.$active &&
    setPosition({ position: "relative", left: "0", bottom: "0", zIndex: 1 })};
  @media (min-width: ${BREAKPOINTS.sm + 1}px) {
    &.expanded {
      height: auto;
    }
    &.shrinked {
      width: 60%;
      height: auto;
    }
  }
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setPosition({
      position: "relative",
      right: "0",
      top: -100,
      zIndex: 5,
    })};
    bottom: unset;
    width: -webkit-fill-available;
    width: -moz-available;
    margin-left: 2rem;
  }
  ${props => props.$desktop && desktop};
  ${props => props.$mobile && mobile};
`;

export const TextContainer = styled.div`
  ${setFlex({})};
  width: 100%;
  ${props => props.$padding && "padding: 2rem;"};
  ${props =>
    props.$introduction &&
    `
    ${setFlex({ align: "flex-end" })};
    height: 35vh;
    @media (max-width: ${BREAKPOINTS.sm}px) {
      ${setFlex({ align: "center" })};
      height: auto;
    }
  `};
`;

export const PDPHeading = styled.div`
  ${props =>
    setFlex({
      justify: props.$justify || "center",
      align: "center",
    })};
  width: 100%;
  ${props => props.$margin && "margin-bottom: 2rem;"};
`;

export const PDPNumber = styled.div`
  ${setFlex({ justify: "center", align: "center" })};
`;

export const PLPContent = styled.div`
  ${setFlex({ direction: "column" })};
  width: -webkit-fill-available;
  width: -moz-available;
  padding: 0 3rem 2.5rem 3rem;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    padding: 2.5rem 1.5rem;
  }
`;

export const PDPTileWrapper = styled.div`
  ${setFlex({ direction: "column" })};
  ${props =>
    props.$count == 0 && `background-color: ${COLOURS.primary.white};`};
`;

export const PDPTile = styled.div`
  ${setPosition({ position: "relative" })}
  ${setFlex({ direction: "column" })};
  ${props => setPDPTileTheme(props.$count)};
  ${props =>
    props.$lastKey &&
    `
    padding-top: 0;
    .roman-count {
      display: none;
    }
  `};
  ${props => props.$secondToLastKey && "padding-bottom: 0"};
`;

export const PDPTileImage = styled.div`
  ${setFlex({ direction: "column" })};
  ${props => setPDPTileImageTheme(props.$count)};
`;

export const PDPRomanNumber = styled.span`
  font-family: gantmodern-regular;
  font-size: 0.8rem;
  ${props => props.$difference && "mix-blend-mode: difference"};
`;

export const PDPContentBox = styled.div`
  ${setPosition({ position: "relative", zIndex: 2 })}
  ${setFlex({ justify: "center", direction: "column" })};
  border: 1px solid ${BORDERS.secondary};
  ${props => setPDPContentTheme(props.$count)};
`;

export const PDPContent = styled.div`
  padding: 2rem;
  ${props => setPDPContent(props.$count)};
  @media (max-width: ${BREAKPOINTS.sm}px) {
    padding: 2rem 1rem;
  }
`;

export const ReturnAnchor = styled.div`
  ${setFlex({ align: "flex-end" })}
  width: 100%;
  padding-bottom: 2rem;
  cursor: pointer;
  margin-top: 5rem;
  ${desktop};
  ${noSelect}

  @media (max-width: ${BREAKPOINTS.sm}px) {
    margin-top: 2rem;
  }
`;

export const LogoContainer = styled.div`
  padding: 4.5rem 0;
  width: 100%;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    padding-top: 0;
  }
`;

export const GantCTA = styled.div`
  padding: 0 2rem 2rem 2rem;
`;
